import { useState } from 'react';
import { IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { Close, MoreVert, Save, VisibilityOutlined, EditOutlined } from '@mui/icons-material';
import {
    GridActionsCellItem,
    GridColDef,
    GridColumnHeaderParams,
    GridRenderCellParams,
    GridRowId,
    GridRowModes,
    GridRowModesModel
} from '@mui/x-data-grid-pro';
import { format } from 'date-fns';
import { IUser } from 'views/backoffice/users/types';
import { AttachmentRow } from './types';

export type GetListColumnsParamProps = {
    onOpenFile: (row: AttachmentRow) => Promise<void>;
    onDeleteFile: (row: AttachmentRow) => Promise<void>;
    onDownloadFile: (row: AttachmentRow) => Promise<void>;
    onClickEdit: (row: GridRowId) => () => void;
    onClickSave: (row: GridRowId) => () => void;
    onClickCancel: (row: GridRowId) => () => void;
    rowModesModel: GridRowModesModel;
    isAllowedDelete: boolean | undefined;
    userListById: Record<number, IUser>;
};

export const getListColumns = ({
    onOpenFile,
    onDeleteFile,
    onDownloadFile,
    onClickEdit,
    onClickSave,
    onClickCancel,
    rowModesModel,
    isAllowedDelete,
    userListById
}: GetListColumnsParamProps) =>
    [
        {
            field: 'actions',
            type: 'actions',
            headerName: '',

            align: 'right',
            sortable: false,
            hideable: false,
            getActions: (params: GridRenderCellParams) => {
                const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
                if (params.row.access === 'editable') {
                    if (isInEditMode) {
                        return [
                            <ActionButton onClick={onClickSave(params.id)} icon={<Save />} label="Save" />,
                            <ActionButton onClick={onClickCancel(params.id)} icon={<Close />} label="Cancel" />
                        ];
                    }

                    return [
                        <ActionButton onClick={onClickEdit(params.id)} icon={<EditOutlined />} label="Edit" />,
                        <ActionButton onClick={() => onOpenFile(params.row)} icon={<VisibilityOutlined />} label="Edit" />
                    ];
                }

                return [<ActionButton onClick={() => onOpenFile(params.row)} icon={<VisibilityOutlined />} label="Edit" />];
            }
        },
        {
            field: 'name',
            headerName: 'File Name',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant="body1" fontSize="16px" color="black" fontWeight={700}>
                    {params.colDef.headerName}
                </Typography>
            ),
            minWidth: 150,
            flex: 1,
            editable: true
        },
        {
            field: 'description',
            headerName: 'Description',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant="body1" fontSize="16px" color="black" fontWeight={700}>
                    {params.colDef.headerName}
                </Typography>
            ),
            width: 150,
            editable: true
        },
        {
            field: 'fileType',
            headerName: 'File Type',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant="body1" fontSize="16px" color="black" fontWeight={700}>
                    {params.colDef.headerName}
                </Typography>
            ),
            width: 125,
            valueFormatter: (params) => params.value?.split('/')[1]?.toUpperCase() || 'unknown'
        },
        {
            field: 'createdAt',
            type: 'datetime',
            headerName: 'Uploaded At',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant="body1" fontSize="16px" color="black" fontWeight={700}>
                    {params.colDef.headerName}
                </Typography>
            ),
            width: 150,
            valueFormatter: (params) => format(new Date(params.value as string), 'MMM dd, yyyy HH:mm:ss')
        },
        {
            field: 'createdBy',
            headerName: 'Uploaded By',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant="body1" fontSize="16px" color="black" fontWeight={700}>
                    {params.colDef.headerName}
                </Typography>
            ),
            width: 150,
            valueFormatter: (params) => (params.value ? userListById[Number(params.value)]?.name || '' : '')
        },
        {
            field: 'recordFieldName',
            headerName: 'Field Name',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant="body1" fontSize="16px" color="black" fontWeight={700}>
                    {params.colDef.headerName}
                </Typography>
            ),
            width: 150
        },
        {
            field: 'endActions',
            headerName: 'Actions',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant="body1" fontSize="16px" color="black" fontWeight={700}>
                    {params.colDef.headerName}
                </Typography>
            ),
            sortable: false,
            hideable: false,
            renderCell: (props) => (
                <EndActionsComponent
                    {...props}
                    isAllowedDelete={isAllowedDelete}
                    onDeleteFile={onDeleteFile}
                    onDownloadFile={onDownloadFile}
                    isAllowedEditable={props.row.access === 'editable'}
                />
            )
        }
    ] as GridColDef[];

export const EndActionsComponent = ({
    onDownloadFile,
    isAllowedDelete,
    onDeleteFile,
    isAllowedEditable,
    ...params
}: GridRenderCellParams & {
    isAllowedDelete?: boolean;
    isAllowedEditable?: boolean;
    onDownloadFile: (row: AttachmentRow) => Promise<void>;
    onDeleteFile: (row: AttachmentRow) => Promise<void>;
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    return (
        <>
            <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
                <MoreVert />
            </IconButton>
            <Menu
                id="file-more-menu"
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => setAnchorEl(null)}
                MenuListProps={{
                    'aria-labelledby': 'file-more-menu',
                    role: 'listbox'
                }}
            >
                <MenuItem onClick={() => onDownloadFile(params.row)}>Download</MenuItem>
                {isAllowedDelete && isAllowedEditable && <MenuItem onClick={() => onDeleteFile(params.row)}>Delete</MenuItem>}
            </Menu>
        </>
    );
};

const ActionButton = ({ icon, label, onClick }: { icon: React.ReactElement; label: string; onClick: () => void }) => (
    <GridActionsCellItem sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }} onClick={onClick} icon={icon} label={label} />
);
